import { useState, useEffect } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { MdUpload } from "react-icons/md";
import { usePerformanceDetails } from "../../../hooks/usePerformanceDetails";

const COLORS = ["#00C2FF", "#FFB800", "#FF6B6B", "#51CF66", "#FF8787", "#845EF7", "#FCC419", "#20C997"];

const formatHour = (hourStr) => {
  if (!hourStr) return "";
  const parts = hourStr.split("T");
  if (parts.length === 2) {
    const timePart = parts[1];
    return timePart.substring(0, 5);
  }
  return hourStr.substring(0, 5);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary-dark border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
        <p className="text-white text-xs sm:text-sm font-medium mb-2">Hour: {label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-gray-300 text-[10px] sm:text-xs mb-1">
            <span style={{ color: entry.color }}>●</span> {entry.name}: {entry.value} MB
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const NetworkOutDetailsGraph = ({ vmIds, date }) => {
  const { getVmDailyMetrics, loading, error } = usePerformanceDetails();
  const [chartData, setChartData] = useState([]);
  const [vmNames, setVmNames] = useState([]);

  useEffect(() => {
    if (date && vmIds && vmIds.length > 0) {
      fetchDailyMetricsData();
    }
  }, [date, vmIds]);

  const fetchDailyMetricsData = async () => {
    try {
      const allVmData = [];
      const names = [];
      
      for (const vmId of vmIds) {
        const result = await getVmDailyMetrics(vmId, date);
        if (result.success && result.data) {
          allVmData.push({
            vmId: result.data.vmId,
            vmName: result.data.vmName,
            hourlyMetrics: result.data.hourlyMetrics
          });
          names.push(result.data.vmName);
        }
      }
      setVmNames(names);

      if (allVmData.length === 0) {
        setChartData([]);
        return;
      }

      const hourMap = new Map();
      
      allVmData.forEach(vm => {
        vm.hourlyMetrics.forEach(metricData => {
          const rawHour = metricData.hour;
          const formattedHour = formatHour(rawHour);
          
          if (!hourMap.has(formattedHour)) {
            hourMap.set(formattedHour, { hour: formattedHour });
          }
          const dataPoint = hourMap.get(formattedHour);
          dataPoint[vm.vmName] = metricData.networkOut;
        });
      });

      const sortedHours = Array.from(hourMap.keys()).sort();
      const transformedData = sortedHours.map(hour => hourMap.get(hour));

      setChartData(transformedData);
    } catch (err) {
      console.error("Error fetching daily metrics data:", err);
    }
  };

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No VM selected</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading network out data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-red-400 text-sm sm:text-base">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
        <MdUpload className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Network Out - {date}</h3>
      </div>

      <div className="mt-4 sm:mt-6">
        {chartData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No network out data available for {date}</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[400px]" style={{ width: "100%", height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="hour" 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#9CA3AF", fontSize: 10 }} 
                    unit=" MB" 
                    width={40}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px" }} />
                  {vmNames.map((vmName, index) => (
                    <Line
                      key={vmName}
                      type="monotone"
                      dataKey={vmName}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3, fill: COLORS[index % COLORS.length] }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NetworkOutDetailsGraph;